import { cn } from '@shared/lib/cn';

import { NOTIFICATION_TEXT } from '../config/notification.text';
import { formatNotifiedAt } from '../lib/format-notified-at';
import { NOTIFICATION_TYPE_LABEL, type NotificationType } from '../model/notification';

export interface NotificationItemProps {
  type: NotificationType;
  notifiedAt: string | null;
  content: string | null;
  read: boolean;
  onSelect(): void;
}

/** 알림 종류 → 이모지(구 `NotificationService` 그대로, DD-56). 장식 — 읽는 이름은 옆 라벨 */
const NOTIFICATION_EMOJI: Record<NotificationType, string> = {
  Memo: '📝',
  File: '📜',
  Data: '📈',
};

const READ_CLASS = {
  unread: '',
  read: 'opacity-(--opacity-muted)',
} as const;

/**
 * 알림 한 건: 머리 줄(종류 · 시각) + 본문(4줄 말줄임). 줄 전체가 버튼이다.
 * 읽음 처리 · 닫기 · 이동은 상위(NotificationList)가 onSelect 에서 한다.
 */
export function NotificationItem({
  type,
  notifiedAt,
  content,
  read,
  onSelect,
}: NotificationItemProps) {
  const label = NOTIFICATION_TYPE_LABEL[type];
  return (
    <button
      type="button"
      data-read={read}
      onClick={onSelect}
      className={cn(
        'flex w-full cursor-pointer flex-col border-b border-border-control pt-2-5 text-left',
        read ? READ_CLASS.read : READ_CLASS.unread,
      )}
    >
      <span className="flex h-5 w-full items-center justify-between pr-2-5 pl-1">
        <span className="flex items-center gap-1">
          <span aria-hidden="true" data-slot="emoji" className="typo-label-sm">
            {NOTIFICATION_EMOJI[type]}
          </span>
          {label ? <span className="typo-label-sm text-fg-default">{label}</span> : null}
        </span>
        {notifiedAt ? (
          <time dateTime={notifiedAt} className="typo-micro text-fg-default">
            {formatNotifiedAt(notifiedAt)}
          </time>
        ) : null}
      </span>
      <span className="sr-only">{read ? NOTIFICATION_TEXT.read : NOTIFICATION_TEXT.unread}</span>
      <span className="w-full py-2-5">
        <span className="line-clamp-4 typo-body text-fg-default">{content}</span>
      </span>
    </button>
  );
}
